import React, { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"

import MainLayout from "../../layouts/MainLayout"
import { createSale, getSalesFormData } from "../../services/salesService"

const SHOP_LOCKED_ROLES = ["gerant_magasin", "gerant_depot_relais"]
const AGENT_ROLES = ["agent_commercial"]

const SALES_UI = {
  surface: "#ffffff",
  border: "#e2e8f0",
  soft: "#f8fafc",
  text: "#0f172a",
  muted: "#64748b",
  primary: "#2563eb",
  success: "#16a34a",
  warning: "#ea580c",
  danger: "#dc2626",
}

function getCurrentUserContext() {
  return {
    role: localStorage.getItem("kongosys_role") || "",
    userId: localStorage.getItem("kongosys_user_id") || "",

    storeId: localStorage.getItem("kongosys_store_id") || "",
    storeCode: localStorage.getItem("kongosys_store_code") || "",
    storeName: localStorage.getItem("kongosys_store_name") || "",

    organizationId: localStorage.getItem("kongosys_organization_id") || "",
    organizationCode: localStorage.getItem("kongosys_organization_code") || "",
    organizationName: localStorage.getItem("kongosys_organization_name") || "",
  }
}

function buildFallbackStore(currentUser) {
  if (!currentUser.storeId) {
    return null
  }

  return {
    id: Number(currentUser.storeId),
    code: currentUser.storeCode || "",
    name: currentUser.storeName || "Shop assigné",
    organization_id: currentUser.organizationId
      ? Number(currentUser.organizationId)
      : null,
    organization_code: currentUser.organizationCode || "",
    organization_name: currentUser.organizationName || "",
  }
}

function toNumber(value) {
  const number = Number(value || 0)
  return Number.isFinite(number) ? number : 0
}

function formatAmount(value) {
  return toNumber(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function getProductCurrency(product) {
  return String(product?.price_currency || "USD").toUpperCase()
}

function getProductExchangeRate(product) {
  return toNumber(product?.exchange_rate || 0)
}

function formatMoney(value, currency = "USD") {
  const normalizedCurrency = String(currency || "USD").toUpperCase()

  return `${formatAmount(value)} ${normalizedCurrency === "CDF" ? "CDF" : "USD"}`
}

function getUsdEquivalentFromCdf(product, cdfValue) {
  const rate = getProductExchangeRate(product)

  if (rate <= 0) return 0

  return toNumber(cdfValue) / rate
}

function getProductShopPriceCdf(product) {
  return toNumber(product?.selling_price_cdf || 0)
}

function getProductFactoryPriceCdf(product) {
  return toNumber(product?.factory_price_cdf || 0)
}

function formatProductMainPrice(product, amountUsd, amountCdf = null) {
  const currency = getProductCurrency(product)
  const cdfValue = amountCdf !== null ? toNumber(amountCdf) : 0

  if (currency === "CDF" && cdfValue > 0) {
    return `${formatMoney(cdfValue, "CDF")} / ${formatMoney(amountUsd, "USD")}`
  }

  return formatMoney(amountUsd, "USD")
}

function buildProductSearchText(product, isAgentUser) {
  if (!product) return ""

  const code = product.code || ""
  const name = product.name || ""
  const brand = product.brand || ""
  const category = product.category || ""
  const stockText = isAgentUser ? "" : `Stock ${toNumber(product.stock)}`

  const priceText = isAgentUser
    ? formatProductMainPrice(
        product,
        getProductTerrainPrice(product),
        getProductShopPriceCdf(product)
      )
    : formatProductMainPrice(
        product,
        getProductShopPrice(product),
        getProductShopPriceCdf(product)
      )

  return [code, name, brand, category, stockText, priceText]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
}

function getProductOptionLabel(product, isAgentUser) {
  if (!product) return ""

  const stockText = isAgentUser ? "" : ` | Stock: ${formatAmount(product.stock)}`
  const priceLabel = isAgentUser ? "Prix terrain" : "Prix shop"
  const priceText = isAgentUser
    ? formatProductMainPrice(product, getProductTerrainPrice(product), getProductShopPriceCdf(product))
    : formatProductMainPrice(product, getProductShopPrice(product), getProductShopPriceCdf(product))

  return `${product.code || ""} - ${product.name || ""}${stockText} | ${priceLabel}: ${priceText}`
}

function getProductTerrainPrice(product) {
  return toNumber(
    product?.terrain_price ??
      product?.default_unit_price ??
      product?.selling_price ??
      product?.shop_selling_price ??
      0
  )
}

function getProductShopPrice(product) {
  return toNumber(
    product?.shop_selling_price ??
      product?.default_unit_price ??
      product?.selling_price ??
      product?.terrain_price ??
      0
  )
}

function getProductDisplayPrice(product, isAgentUser) {
  return isAgentUser
    ? getProductTerrainPrice(product)
    : getProductShopPrice(product)
}

function getProductFactoryPrice(product) {
  return toNumber(product?.special_factory_price ?? product?.factory_price ?? 0)
}

function getProductPriceOptions(product, allowSpecialPrice, isAgentUser) {
  if (!product) {
    return []
  }

  if (isAgentUser) {
    const terrainPrice = getProductTerrainPrice(product)

    if (terrainPrice > 0) {
      return [
        {
          mode: "agent_proposed",
          label: "Prix proposé agent",
          price: terrainPrice,
        },
      ]
    }

    return []
  }

  if (Array.isArray(product.price_options) && product.price_options.length > 0) {
    return product.price_options.filter((option) => {
      if (option.mode === "factory_special") {
        return Boolean(allowSpecialPrice)
      }

      return true
    })
  }

  const options = []
  const shopPrice = getProductShopPrice(product)
  const factoryPrice = getProductFactoryPrice(product)

  if (shopPrice > 0) {
    options.push({
      mode: "shop",
      label: "Prix shop",
      price: shopPrice,
    })
  }

  if (
    allowSpecialPrice &&
    factoryPrice > 0 &&
    Math.abs(factoryPrice - shopPrice) > 0.005
  ) {
    options.push({
      mode: "factory_special",
      label: "Prix spécial usine",
      price: factoryPrice,
    })
  }

  return options
}

function findPriceOption(product, mode, allowSpecialPrice, isAgentUser) {
  const options = getProductPriceOptions(product, allowSpecialPrice, isAgentUser)

  return options.find((option) => option.mode === mode) || options[0] || null
}

function SectionTitle({ eyebrow, title, description }) {
  return (
    <Box>
      {eyebrow ? (
        <Typography
          variant="overline"
          sx={{
            display: "block",
            color: SALES_UI.primary,
            fontWeight: 900,
            letterSpacing: 0.9,
            lineHeight: 1.2,
            mb: 0.35,
          }}
        >
          {eyebrow}
        </Typography>
      ) : null}

      <Typography
        sx={{
          color: SALES_UI.text,
          fontSize: { xs: 18, sm: 20 },
          fontWeight: 900,
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>

      {description ? (
        <Typography
          variant="body2"
          sx={{
            color: SALES_UI.muted,
            mt: 0.4,
          }}
        >
          {description}
        </Typography>
      ) : null}
    </Box>
  )
}


function SummaryPill({ label, value }) {
  return (
    <Box
      sx={{
        p: 1.4,
        minWidth: 0,
        borderRadius: 2.5,
        backgroundColor: SALES_UI.soft,
        border: `1px solid ${SALES_UI.border}`,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: SALES_UI.muted,
          fontWeight: 700,
        }}
      >
        {label}
      </Typography>

      <Typography
        noWrap
        sx={{
          color: SALES_UI.text,
          fontWeight: 900,
          mt: 0.25,
        }}
      >
        {value}
      </Typography>
    </Box>
  )
}


export default function SalesPage({ embedded = false, onClose, onSaved }) {
  const currentUser = getCurrentUserContext()

  // En mode embedded, le formulaire est affiché dans une fenêtre modale
  // depuis l'historique des ventes. La route /sales/new reste compatible.
  const Layout = embedded ? React.Fragment : MainLayout

  const isShopUser =
    SHOP_LOCKED_ROLES.includes(currentUser.role) && Boolean(currentUser.storeId)

  const isAgentUser = AGENT_ROLES.includes(currentUser.role)

  const canUseSpecialPrice = !isShopUser && !isAgentUser
  const canApplyDiscount = !isShopUser && !isAgentUser

  const lockedStoreId = isShopUser ? String(currentUser.storeId) : ""

  const [products, setProducts] = useState([])
  const [customers, setCustomers] = useState([])
  const [stores, setStores] = useState([])

  const [loading, setLoading] = useState(false)
  const [savingSale, setSavingSale] = useState(false)

  const [saleMode, setSaleMode] = useState(isAgentUser ? "agent_order" : "direct")
  const [paymentStatus, setPaymentStatus] = useState("unpaid")
  const [paidAmount, setPaidAmount] = useState("0")

  const [managerContacted, setManagerContacted] = useState("no")
  const [managerName, setManagerName] = useState("")

  const [customerId, setCustomerId] = useState("")
  const [storeId, setStoreId] = useState(lockedStoreId)
  const [notes, setNotes] = useState("")

  const [selectedProductId, setSelectedProductId] = useState("")
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [priceMode, setPriceMode] = useState(
    isAgentUser ? "agent_proposed" : "shop"
  )

  const [discountType, setDiscountType] = useState("none")
  const [discountValue, setDiscountValue] = useState("0")

  const [quantity, setQuantity] = useState("1")
  const [unitPrice, setUnitPrice] = useState("")
  const [lineTotal, setLineTotal] = useState("")

  const [cart, setCart] = useState([])
  const [tva, setTva] = useState("0")
  const [editingItemId, setEditingItemId] = useState(null)

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  })

  useEffect(() => {
    loadInitialData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, severity, message })
  }

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  const getDefaultPriceMode = () => {
    return isAgentUser ? "agent_proposed" : "shop"
  }

  const calculateLine = (priceValue, qtyValue = quantity) => {
    const price = toNumber(priceValue)
    const qty = Math.max(0, toNumber(qtyValue))

    setLineTotal((price * qty).toFixed(2))
  }

  const applyPriceToLine = (
    product,
    mode,
    qtyValue = quantity,
    nextDiscountType = discountType,
    nextDiscountValue = discountValue
  ) => {
    const finalMode = canUseSpecialPrice ? mode : getDefaultPriceMode()

    const option = findPriceOption(
      product,
      finalMode,
      canUseSpecialPrice,
      isAgentUser
    )

    if (!option) {
      setPriceMode(getDefaultPriceMode())
      setUnitPrice("")
      setLineTotal("")
      return
    }

    const qty = Math.max(0, toNumber(qtyValue))
    const basePrice = toNumber(option.price)
    let finalPrice = basePrice

    if (canApplyDiscount) {
      const discount = toNumber(nextDiscountValue)

      if (nextDiscountType === "percent" && discount > 0) {
        finalPrice = Math.max(0, basePrice - (basePrice * discount) / 100)
      }

      if (nextDiscountType === "amount" && discount > 0) {
        finalPrice = Math.max(0, basePrice - discount)
      }
    }

    setPriceMode(option.mode)
    setUnitPrice(finalPrice.toFixed(2))
    setLineTotal((finalPrice * qty).toFixed(2))
  }

  const loadInitialData = async () => {
    try {
      setLoading(true)

      const effectiveStoreId = isShopUser ? lockedStoreId : storeId || null
      const data = await getSalesFormData(
        isAgentUser ? undefined : effectiveStoreId || undefined
      )

      setCustomers(data.customers || [])

      if (isAgentUser) {
        setStores([])
        setStoreId("")
        setProducts(data.products || [])
        return
      }

      if (isShopUser) {
        const backendStores = Array.isArray(data.stores) ? data.stores : []

        const foundStore = backendStores.find(
          (store) => String(store.id) === String(lockedStoreId)
        )

        const fallbackStore = buildFallbackStore(currentUser)

        if (foundStore) {
          setStores([foundStore])
        } else if (fallbackStore) {
          setStores([fallbackStore])
        } else {
          setStores([])
        }

        setStoreId(lockedStoreId)
        setProducts(data.products || [])
      } else {
        setStores(data.stores || [])
        setProducts(data.products || [])
      }
    } catch (err) {
      console.error(err)
      showSnackbar(
        err?.response?.data?.detail || "Erreur chargement données",
        "error"
      )
    } finally {
      setLoading(false)
    }
  }

  const loadProductsByStore = async (selectedStoreId) => {
    if (isAgentUser) {
      await loadInitialData()
      return
    }

    if (!selectedStoreId) {
      setProducts([])
      return
    }

    try {
      setLoading(true)

      const finalStoreId = isShopUser ? lockedStoreId : selectedStoreId
      const data = await getSalesFormData(finalStoreId)

      setCustomers(data.customers || [])
      setProducts(data.products || [])

      if (isShopUser) {
        const backendStores = Array.isArray(data.stores) ? data.stores : []
        const foundStore = backendStores.find(
          (store) => String(store.id) === String(lockedStoreId)
        )

        const fallbackStore = buildFallbackStore(currentUser)

        if (foundStore) {
          setStores([foundStore])
        } else if (fallbackStore) {
          setStores([fallbackStore])
        }
      }
    } catch (err) {
      console.error(err)
      showSnackbar(
        err?.response?.data?.detail || "Erreur chargement produits",
        "error"
      )
    } finally {
      setLoading(false)
    }
  }

  const resetLineForm = () => {
    setSelectedProductId("")
    setSelectedProduct(null)
    setPriceMode(getDefaultPriceMode())
    setDiscountType("none")
    setDiscountValue("0")
    setQuantity("1")
    setUnitPrice("")
    setLineTotal("")
    setEditingItemId(null)
  }

  const resetSaleForm = async () => {
    setCustomerId("")
    setStoreId(isShopUser ? lockedStoreId : "")
    setNotes("")
    setCart([])
    setTva("0")
    setSaleMode(isAgentUser ? "agent_order" : "direct")
    setPaymentStatus("unpaid")
    setPaidAmount("0")
    setManagerContacted("no")
    setManagerName("")
    resetLineForm()

    if (isShopUser && lockedStoreId) {
      await loadProductsByStore(lockedStoreId)
    } else {
      await loadInitialData()
    }
  }

  const handleStoreChange = async (e) => {
    if (isShopUser || isAgentUser) {
      return
    }

    const value = e.target.value

    setStoreId(value)
    setCart([])
    resetLineForm()

    if (value) {
      await loadProductsByStore(value)
    } else {
      setProducts([])
    }
  }

  const handleProductChange = (e) => {
    const value = e.target.value
    const productId = value === "" ? "" : Number(value)
    const product =
      products.find((p) => Number(p.id) === Number(productId)) || null

    setSelectedProductId(value)
    setSelectedProduct(product)

    if (product) {
      applyPriceToLine(product, getDefaultPriceMode())
    } else {
      setPriceMode(getDefaultPriceMode())
      setUnitPrice("")
      setLineTotal("")
    }
  }


  const handleProductAutocompleteChange = (_event, product) => {
    const value = product?.id ? String(product.id) : ""

    setSelectedProductId(value)
    setSelectedProduct(product || null)

    if (product) {
      applyPriceToLine(product, getDefaultPriceMode())
    } else {
      setPriceMode(getDefaultPriceMode())
      setUnitPrice("")
      setLineTotal("")
    }
  }

  const handlePriceModeChange = (e) => {
    if (!canUseSpecialPrice) {
      applyPriceToLine(selectedProduct, getDefaultPriceMode())
      return
    }

    const mode = e.target.value
    applyPriceToLine(selectedProduct, mode)
  }

  const handleUnitPriceChange = (e) => {
    const value = e.target.value

    setUnitPrice(value)
    calculateLine(value)
  }

  const handleDiscountTypeChange = (e) => {
    const value = e.target.value
    setDiscountType(value)

    if (value === "none") {
      setDiscountValue("0")
    }

    if (selectedProduct) {
      applyPriceToLine(
        selectedProduct,
        priceMode,
        quantity,
        value,
        value === "none" ? "0" : discountValue
      )
    }
  }

  const handleDiscountValueChange = (e) => {
    const value = e.target.value
    setDiscountValue(value)

    if (selectedProduct) {
      applyPriceToLine(
        selectedProduct,
        priceMode,
        quantity,
        discountType,
        value
      )
    }
  }

  const handleQuantityChange = (e) => {
    const value = e.target.value

    setQuantity(value)

    if (selectedProduct) {
      calculateLine(unitPrice, value)
    } else {
      setLineTotal("")
    }
  }

  const buildCartItem = () => {
    if (!selectedProduct) {
      return null
    }

    const selectedQty = toNumber(quantity)
    const availableStock = toNumber(selectedProduct.stock)

    const selectedOption = findPriceOption(
      selectedProduct,
      priceMode,
      canUseSpecialPrice,
      isAgentUser
    )

    if (!selectedOption) {
      showSnackbar("Aucun prix valide pour ce produit.", "warning")
      return null
    }

    const basePrice = toNumber(selectedOption.price)
    const finalPrice = toNumber(unitPrice)

    if (finalPrice <= 0) {
      showSnackbar("Le prix proposé doit être supérieur à 0.", "warning")
      return null
    }

    const discountAmount = Math.max(0, basePrice - finalPrice)

    return {
      id: editingItemId || Date.now(),
      product_id: selectedProduct.id,
      product_code: selectedProduct.code,
      name: selectedProduct.name,
      base_price: basePrice,
      price: finalPrice,
      price_mode: isAgentUser ? "agent_proposed" : selectedOption.mode,
      price_label: isAgentUser ? "Prix proposé agent" : selectedOption.label,
      discount_type: isAgentUser
        ? discountAmount > 0
          ? "proposed_negotiation"
          : "none"
        : canApplyDiscount
        ? discountType
        : "none",
      discount_value: isAgentUser
        ? discountAmount
        : canApplyDiscount
        ? toNumber(discountValue)
        : 0,
      discount_amount: discountAmount,
      quantity: selectedQty,
      total: Number((finalPrice * selectedQty).toFixed(2)),
      stock: isAgentUser ? null : availableStock,
      stock_warning: !isAgentUser && selectedQty > availableStock,
      negative_after_sale: Number((availableStock - selectedQty).toFixed(2)),
    }
  }

  const commitCartItem = (item) => {
    if (!item) {
      return
    }

    if (editingItemId) {
      setCart((prev) =>
        prev.map((row) => (row.id === editingItemId ? item : row))
      )

      showSnackbar("Ligne modifiée")
    } else {
      const existingItem = cart.find((row) => row.product_id === item.product_id)

      if (existingItem) {
        showSnackbar(
          "Ce produit existe déjà dans le panier. Utilisez Edit.",
          "warning"
        )
        return
      }

      setCart((prev) => [...prev, item])
      showSnackbar("Produit ajouté au panier")
    }

    resetLineForm()
  }

  const addOrUpdateCart = () => {
    const finalStoreId = isShopUser ? lockedStoreId : storeId

    if (!isAgentUser && !finalStoreId) {
      showSnackbar("Veuillez sélectionner un magasin.", "warning")
      return
    }

    if (!selectedProduct) {
      showSnackbar("Veuillez sélectionner un produit", "warning")
      return
    }

    const selectedQty = toNumber(quantity)

    if (!selectedQty || selectedQty <= 0) {
      showSnackbar("La quantité doit être supérieure à 0", "warning")
      return
    }

    const item = buildCartItem()

    if (!item) {
      return
    }

    if (!isAgentUser && saleMode === "direct" && item.stock_warning) {
      showSnackbar(
        `Stock insuffisant pour ${item.name}. Disponible : ${item.stock}, demandé : ${item.quantity}. Utilisez le mode Commande si le client accepte d'attendre.`,
        "error"
      )
      return
    }

    commitCartItem(item)
  }

  const handleEditItem = (item) => {
    const product =
      products.find((p) => Number(p.id) === Number(item.product_id)) || null

    setEditingItemId(item.id)
    setSelectedProductId(String(item.product_id))
    setSelectedProduct(product)
    setPriceMode(item.price_mode || getDefaultPriceMode())
    setDiscountType(item.discount_type || "none")
    setDiscountValue(String(item.discount_value || 0))
    setQuantity(String(item.quantity))
    setUnitPrice(toNumber(item.price).toFixed(2))
    setLineTotal(toNumber(item.total).toFixed(2))
  }

  const removeItem = (id) => {
    setCart((prev) => prev.filter((item) => item.id !== id))

    if (editingItemId === id) {
      resetLineForm()
    }

    showSnackbar("Ligne supprimée")
  }

  const totalHT = useMemo(() => {
    return cart.reduce((sum, item) => sum + toNumber(item.total), 0)
  }, [cart])

  const tvaRate = Math.max(0, toNumber(tva))

  const tvaAmount = useMemo(() => {
    return isAgentUser ? 0 : (totalHT * tvaRate) / 100
  }, [isAgentUser, totalHT, tvaRate])

  const totalTTC = useMemo(() => {
    return totalHT + tvaAmount
  }, [totalHT, tvaAmount])

  const totalItems = useMemo(() => {
    return cart.reduce((sum, item) => sum + toNumber(item.quantity), 0)
  }, [cart])

  const cartWarnings = useMemo(() => {
    return cart.filter((item) => item.stock_warning)
  }, [cart])

  const calculatedPaidAmount = useMemo(() => {
    if (isAgentUser) {
      return 0
    }

    if (paymentStatus === "paid") {
      return totalTTC
    }

    if (paymentStatus === "unpaid") {
      return 0
    }

    return toNumber(paidAmount)
  }, [isAgentUser, paymentStatus, paidAmount, totalTTC])

  const remainingAmount = useMemo(() => {
    return Math.max(0, totalTTC - calculatedPaidAmount)
  }, [totalTTC, calculatedPaidAmount])

  const overpaidAmount = useMemo(() => {
    return Math.max(0, calculatedPaidAmount - totalTTC)
  }, [calculatedPaidAmount, totalTTC])

  const hasNegotiatedPrice = useMemo(() => {
    return cart.some(
      (item) => Math.abs(toNumber(item.price) - toNumber(item.base_price)) > 0.005
    )
  }, [cart])

  const saveSale = async () => {
    const finalStoreId = isAgentUser
      ? null
      : isShopUser
      ? Number(lockedStoreId)
      : Number(storeId)

    if (!isAgentUser && !finalStoreId) {
      showSnackbar("Aucun magasin n'est lié à cette opération.", "error")
      return
    }

    if (isAgentUser && !customerId) {
      showSnackbar("L'agent commercial doit sélectionner un client.", "warning")
      return
    }

    if (cart.length === 0) {
      showSnackbar("Ajoutez au moins un produit au panier", "warning")
      return
    }

    if (isAgentUser && hasNegotiatedPrice && managerContacted !== "yes") {
      showSnackbar(
        "Prix négocié détecté. Indiquez que le responsable Entreprise principale a été contacté.",
        "warning"
      )
      return
    }

    if (!isAgentUser && saleMode === "direct" && cartWarnings.length > 0) {
      showSnackbar(
        "Vente directe impossible : stock insuffisant. Utilisez Commande client ou réapprovisionnez le stock.",
        "error"
      )
      return
    }

    if (!isAgentUser && paymentStatus === "partial" && calculatedPaidAmount <= 0) {
      showSnackbar(
        "Pour un paiement partiel, saisissez le montant payé.",
        "warning"
      )
      return
    }

    if (!isAgentUser && calculatedPaidAmount > totalTTC) {
      showSnackbar(
        "Le montant payé ne peut pas dépasser le total de la vente.",
        "warning"
      )
      return
    }

    const payload = {
      customer_id: customerId ? Number(customerId) : null,
      store_id: isAgentUser ? null : finalStoreId,
      sale_type: isAgentUser ? "order" : saleMode === "order" ? "order" : "direct",
      order_source: isAgentUser ? "agent_commercial" : "standard",
      status: isAgentUser
        ? "pending_price_validation"
        : saleMode === "order"
        ? "pending"
        : "confirmed",
      payment_status: isAgentUser ? "unpaid" : paymentStatus,
      paid_amount: isAgentUser ? 0 : Number(calculatedPaidAmount.toFixed(2)),
      vat_percent: isAgentUser ? 0 : Number(tva || 0),
      notes: notes?.trim() || null,
      allow_negative_stock: false,
      manager_contacted: isAgentUser ? managerContacted === "yes" : false,
      manager_name: isAgentUser ? managerName.trim() || null : null,
      agent_id: isAgentUser ? Number(currentUser.userId || 0) : null,
      items: cart.map((item) => ({
        product_id: Number(item.product_id),
        quantity: Number(item.quantity),
        unit_price: Number(item.price),
        requested_unit_price: isAgentUser ? Number(item.price) : null,
        customer_requested_price: isAgentUser ? Number(item.price) : null,
        price_mode: item.price_mode,
        base_price: Number(item.base_price || item.price),
        discount_type: item.discount_type || "none",
        discount_value: Number(item.discount_value || 0),
        discount_amount: Number(item.discount_amount || 0),
      })),
    }

    try {
      setSavingSale(true)

      const result = await createSale(payload)

      showSnackbar(
        isAgentUser
          ? `Opportunité terrain enregistrée - ${result.sale_code || ""}`
          : saleMode === "order"
          ? `Commande enregistrée avec succès - ${result.sale_code || ""}`
          : `Vente enregistrée avec succès - ${result.sale_code || ""}`,
        "success"
      )

      await resetSaleForm()

      if (typeof onSaved === "function") {
        onSaved(result)
      }
    } catch (error) {
      console.error("Erreur création vente:", error)

      const message =
        error?.response?.data?.detail || "Erreur lors de l'enregistrement"

      showSnackbar(message, "error")
    } finally {
      setSavingSale(false)
    }
  }

  const selectedStoreName =
    stores.find((store) => String(store.id) === String(storeId))?.name ||
    currentUser.storeName ||
    "-"

  const selectedCustomerName =
    customers.find((customer) => String(customer.id) === String(customerId))
      ?.name || "Client non sélectionné"

  const finalStoreSelected = isAgentUser
    ? true
    : isShopUser
    ? Boolean(lockedStoreId)
    : Boolean(storeId)

  const priceOptions = getProductPriceOptions(
    selectedProduct,
    canUseSpecialPrice,
    isAgentUser
  )

  const showSpecialPriceWarning =
    canUseSpecialPrice && priceMode === "factory_special"

  const showDiscountWarning =
    canApplyDiscount && discountType !== "none" && toNumber(discountValue) > 0

  return (
    <Layout>
      <Box
        sx={{
          maxWidth: embedded ? "100%" : 1540,
          width: "100%",
          mx: "auto",
          pb: embedded ? 0 : 3,
        }}
      >
        <Box sx={{ mb: 2.5, display: embedded ? "none" : "block" }}>
          <Typography
            variant="overline"
            sx={{
              display: "block",
              color: SALES_UI.primary,
              fontWeight: 900,
              letterSpacing: 1,
              lineHeight: 1.2,
              mb: 0.5,
            }}
          >
            VENTES
          </Typography>

          <Typography
            sx={{
              color: SALES_UI.text,
              fontSize: { xs: 27, sm: 31, md: 35 },
              fontWeight: 900,
              lineHeight: 1.08,
            }}
          >
            {isAgentUser ? "Nouvelle opportunité terrain" : "Nouvelle vente / commande"}
          </Typography>

          <Typography variant="body2" sx={{ color: SALES_UI.muted, mt: 0.6, maxWidth: 900 }}>
            {isAgentUser
              ? "L'agent enregistre le client, les articles et le prix proposé. Entreprise principale valide ensuite le prix final."
              : isShopUser
              ? `${currentUser.organizationName || "Organisation"} • ${
                  currentUser.storeCode
                    ? `${currentUser.storeCode} - ${currentUser.storeName}`
                    : currentUser.storeName || "Shop assigné"
                }`
              : "Vente directe si le stock est disponible. Commande client si le client paie et attend la livraison."}
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: embedded
              ? { xs: 1.25, sm: 1.75, md: 2 }
              : { xs: 1.5, sm: 2.25, md: 3 },
            maxWidth: embedded ? "100%" : 1380,
            borderRadius: 3.5,
            border: `1px solid ${SALES_UI.border}`,
            boxShadow: "0 12px 32px rgba(15, 23, 42, 0.05)",
            backgroundColor: SALES_UI.surface,
          }}
        >
          <Stack spacing={embedded ? 1.75 : 2.5}>
            {isAgentUser && (
              <Alert severity="info" variant="outlined">
                Vous créez seulement une opportunité terrain. Aucun paiement,
                aucune dette et aucune sortie de stock ne sont créés ici. Kin
                Mousse verra automatiquement que cette opportunité vient de vous.
              </Alert>
            )}

            {isShopUser && (
              <Alert severity="info" variant="outlined">
                Vous êtes connecté comme gérant. Les ventes seront enregistrées
                uniquement sur votre shop :{" "}
                <strong>
                  {currentUser.storeCode
                    ? `${currentUser.storeCode} - ${currentUser.storeName}`
                    : currentUser.storeName}
                </strong>
                . Le prix usine est masqué. Le prix appliqué est le{" "}
                <strong>prix shop</strong>.
              </Alert>
            )}

            {!isAgentUser && (
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  select
                  label="Type d'opération"
                  value={saleMode}
                  onChange={(e) => {
                    setSaleMode(e.target.value)
                    setCart([])
                    resetLineForm()
                  }}
                  disabled={savingSale}
                >
                  <MenuItem value="direct">Vente directe</MenuItem>
                  <MenuItem value="order">Commande client</MenuItem>
                </TextField>

                <TextField
                  fullWidth
                  select
                  label="Statut paiement"
                  value={paymentStatus}
                  onChange={(e) => {
                    setPaymentStatus(e.target.value)

                    if (e.target.value !== "partial") {
                      setPaidAmount("0")
                    }
                  }}
                  disabled={savingSale}
                >
                  <MenuItem value="paid">Payé totalement</MenuItem>
                  <MenuItem value="partial">Paiement partiel</MenuItem>
                  <MenuItem value="unpaid">Non payé</MenuItem>
                </TextField>

                {paymentStatus === "partial" && (
                  <TextField
                    fullWidth
                    label="Montant payé"
                    type="number"
                    value={paidAmount}
                    onChange={(e) => setPaidAmount(e.target.value)}
                    slotProps={{
                      htmlInput: {
                        min: 0,
                        step: "0.01",
                      },
                    }}
                    disabled={savingSale}
                  />
                )}
              </Stack>
            )}

            {isAgentUser && (
              <TextField
                fullWidth
                select
                label="Client amené par l'agent"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                disabled={savingSale}
                required
                helperText="Créer le client dans Clients si la liste est vide."
              >
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={String(customer.id)}>
                    {customer.code
                      ? `${customer.code} - ${customer.name}`
                      : customer.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {!isAgentUser && (
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  select
                  label="Magasin"
                  value={storeId}
                  onChange={handleStoreChange}
                  disabled={savingSale || isShopUser}
                  helperText={
                    isShopUser
                      ? "Magasin verrouillé automatiquement selon votre compte"
                      : "Sélectionnez le magasin concerné"
                  }
                >
                  {stores.map((store) => (
                    <MenuItem key={store.id} value={String(store.id)}>
                      {store.code ? `${store.code} - ${store.name}` : store.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  select
                  label="Client"
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                  disabled={savingSale}
                >
                  <MenuItem value="">Walk-in customer / Vente directe</MenuItem>

                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={String(customer.id)}>
                      {customer.code
                        ? `${customer.code} - ${customer.name}`
                        : customer.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            )}

            {isAgentUser && (
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  select
                  label="Responsable Entreprise principale contacté ?"
                  value={managerContacted}
                  onChange={(e) => setManagerContacted(e.target.value)}
                  disabled={savingSale}
                  helperText="Obligatoire si le prix proposé est différent du prix terrain."
                >
                  <MenuItem value="no">Non</MenuItem>
                  <MenuItem value="yes">Oui</MenuItem>
                </TextField>

                <TextField
                  fullWidth
                  label="Nom du responsable contacté"
                  value={managerName}
                  onChange={(e) => setManagerName(e.target.value)}
                  disabled={savingSale || managerContacted !== "yes"}
                  placeholder="Exemple : Responsable commercial, DGA, Admin..."
                />
              </Stack>
            )}

            <TextField
              fullWidth
              label={isAgentUser ? "Notes terrain" : "Notes"}
              multiline
              minRows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={
                isAgentUser
                  ? "Besoin du client, quantité souhaitée, prix discuté, urgence, responsable appelé..."
                  : "Notes sur la vente ou la commande..."
              }
              disabled={savingSale}
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: isAgentUser
                    ? "repeat(3, minmax(0, 1fr))"
                    : "repeat(4, minmax(0, 1fr))",
                },
                gap: 1.25,
              }}
            >
              <SummaryPill
                label="Mode"
                value={
                  isAgentUser
                    ? "Opportunité terrain"
                    : saleMode === "direct"
                    ? "Vente directe"
                    : "Commande client"
                }
              />

              {!isAgentUser && (
                <SummaryPill
                  label="Magasin"
                  value={selectedStoreName}
                />
              )}

              <SummaryPill
                label="Client"
                value={selectedCustomerName}
              />

              <SummaryPill
                label="Articles"
                value={formatAmount(totalItems)}
              />
            </Box>

            {cartWarnings.length > 0 && saleMode === "order" && !isAgentUser && (
              <Alert severity="warning" variant="outlined">
                {cartWarnings.length} article(s) ont un stock insuffisant. Cette
                commande sera enregistrée sans sortie de stock immédiate.
              </Alert>
            )}

            <Divider />

            <SectionTitle
              eyebrow="ARTICLES"
              title="Ajouter un produit"
              description="Recherchez un produit, contrôlez le prix et la quantité puis ajoutez-le au panier."
            />

            <Autocomplete
              fullWidth
              options={products}
              value={selectedProduct}
              onChange={handleProductAutocompleteChange}
              disabled={loading || !finalStoreSelected || savingSale}
              autoHighlight
              clearOnEscape
              noOptionsText="Aucun produit trouvé"
              getOptionLabel={(option) => getProductOptionLabel(option, isAgentUser)}
              isOptionEqualToValue={(option, value) =>
                Number(option?.id) === Number(value?.id)
              }
              filterOptions={(options, state) => {
                const keyword = String(state.inputValue || "")
                  .trim()
                  .toLowerCase()

                if (!keyword) {
                  return options.slice(0, 50)
                }

                return options
                  .filter((product) =>
                    buildProductSearchText(product, isAgentUser).includes(keyword)
                  )
                  .slice(0, 50)
              }}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  key={option.id}
                  sx={{ display: "flex", flexDirection: "column", alignItems: "stretch" }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={0.75}
                    justifyContent="space-between"
                    sx={{ width: "100%" }}
                  >
                    <Box>
                      <Typography fontWeight={800} variant="body2">
                        {option.code} - {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[option.category, option.brand].filter(Boolean).join(" • ") || "Produit"}
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                      {!isAgentUser && (
                        <Typography variant="caption" fontWeight={800}>
                          Stock : {formatAmount(option.stock)}
                        </Typography>
                      )}
                      <Typography variant="caption" display="block" color="primary.main" fontWeight={900}>
                        {isAgentUser ? "Prix terrain" : "Prix shop"} :{" "}
                        {isAgentUser
                          ? formatProductMainPrice(
                              option,
                              getProductTerrainPrice(option),
                              getProductShopPriceCdf(option)
                            )
                          : formatProductMainPrice(
                              option,
                              getProductShopPrice(option),
                              getProductShopPriceCdf(option)
                            )}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Recherche produit"
                  placeholder="Tapez code, nom, marque ou catégorie..."
                  helperText={
                    !finalStoreSelected
                      ? "Sélectionnez d'abord un magasin"
                      : isShopUser
                      ? "Tapez le nom ou le code. Le prix shop est appliqué automatiquement."
                      : isAgentUser
                      ? "Tapez le nom ou le code. Le prix terrain sert de référence avant validation."
                      : "Tapez le nom ou le code pour vendre rapidement."
                  }
                />
              )}
            />

            {selectedProduct && (
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                <Chip label={`Code: ${selectedProduct.code}`} size="small" />

                <Chip
                  label={`${isAgentUser ? "Prix terrain référence" : "Prix shop"}: ${formatProductMainPrice(
                    selectedProduct,
                    getProductDisplayPrice(selectedProduct, isAgentUser),
                    getProductShopPriceCdf(selectedProduct)
                  )}`}
                  color="primary"
                  size="small"
                />

                {!isAgentUser &&
                  canUseSpecialPrice &&
                  getProductFactoryPrice(selectedProduct) > 0 && (
                    <Chip
                      label={`Prix spécial usine: ${formatProductMainPrice(
                        selectedProduct,
                        getProductFactoryPrice(selectedProduct),
                        getProductFactoryPriceCdf(selectedProduct)
                      )}`}
                      color="warning"
                      size="small"
                    />
                  )}

                {!isAgentUser && (
                  <Chip
                    label={`Stock: ${toNumber(selectedProduct.stock)}`}
                    color={toNumber(selectedProduct.stock) > 0 ? "success" : "error"}
                    size="small"
                  />
                )}
              </Stack>
            )}

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              {!isAgentUser && (
                <TextField
                  fullWidth
                  select
                  label="Option de prix"
                  value={priceMode}
                  onChange={handlePriceModeChange}
                  disabled={
                    !selectedProduct ||
                    savingSale ||
                    !canUseSpecialPrice ||
                    priceOptions.length <= 1
                  }
                  helperText={
                    isShopUser
                      ? "Prix shop obligatoire pour le gérant."
                      : "Prix normal ou prix spécial usine."
                  }
                >
                  {priceOptions.map((option) => (
                    <MenuItem key={option.mode} value={option.mode}>
                      {option.label} — {formatProductMainPrice(
                        selectedProduct,
                        option.price,
                        option.mode === "factory_special"
                          ? getProductFactoryPriceCdf(selectedProduct)
                          : getProductShopPriceCdf(selectedProduct)
                      )}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {canApplyDiscount && (
                <TextField
                  fullWidth
                  select
                  label="Remise"
                  value={discountType}
                  onChange={handleDiscountTypeChange}
                  disabled={!selectedProduct || savingSale}
                >
                  <MenuItem value="none">Aucune remise</MenuItem>
                  <MenuItem value="percent">Remise en %</MenuItem>
                  <MenuItem value="amount">Remise en montant</MenuItem>
                </TextField>
              )}

              {canApplyDiscount && discountType !== "none" && (
                <TextField
                  fullWidth
                  label={discountType === "percent" ? "Remise (%)" : "Remise montant"}
                  type="number"
                  value={discountValue}
                  onChange={handleDiscountValueChange}
                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: "0.01",
                    },
                  }}
                  disabled={!selectedProduct || savingSale}
                />
              )}

              <TextField
                fullWidth
                label={isAgentUser ? "Prix proposé au client" : "Prix unitaire"}
                value={unitPrice}
                onChange={handleUnitPriceChange}
                type="number"
                disabled={!isAgentUser || !selectedProduct || savingSale}
                helperText={
                  isAgentUser
                    ? "Prix proposé seulement. Validation finale côté Entreprise principale."
                    : ""
                }
                slotProps={{
                  htmlInput: {
                    min: 0,
                    step: "0.01",
                  },
                }}
              />

              <TextField
                fullWidth
                label="Quantité"
                type="number"
                value={quantity}
                onChange={handleQuantityChange}
                slotProps={{
                  htmlInput: {
                    min: 1,
                    step: 1,
                  },
                }}
                disabled={!selectedProduct || savingSale}
              />

              <TextField fullWidth label="Total ligne" value={lineTotal} disabled />
            </Stack>

            {isAgentUser && selectedProduct && (
              <Alert severity="info" variant="outlined">
                Prix terrain référence :{" "}
                <strong>{formatProductMainPrice(selectedProduct, getProductTerrainPrice(selectedProduct), getProductShopPriceCdf(selectedProduct))}</strong>.
                Prix proposé : <strong>{formatMoney(unitPrice, "USD")}</strong>. La
                différence sera soumise à validation.
              </Alert>
            )}

            {showSpecialPriceWarning && (
              <Alert severity="warning" variant="outlined">
                Vous utilisez le <strong>prix spécial usine</strong>. Cette
                option doit correspondre à un client spécial ou à une
                autorisation interne.
              </Alert>
            )}

            {showDiscountWarning && (
              <Alert severity="warning" variant="outlined">
                Une remise est appliquée sur cette ligne. Elle sera conservée
                dans l'historique de la commande.
              </Alert>
            )}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{
                "& .MuiButton-root": {
                  minHeight: 42,
                  borderRadius: 2.5,
                  fontWeight: 800,
                },
              }}
            >
              <Button
                variant="contained"
                onClick={addOrUpdateCart}
                disabled={!selectedProduct || savingSale}
              >
                {editingItemId ? "Modifier la ligne" : "Ajouter au panier"}
              </Button>

              {editingItemId && (
                <Button variant="outlined" onClick={resetLineForm} disabled={savingSale}>
                  Annuler modification
                </Button>
              )}

              {loading && <CircularProgress size={24} />}
            </Stack>

            <Divider />

            <SectionTitle
              eyebrow="PANIER"
              title="Articles sélectionnés"
              description="Vérifiez les prix, quantités, écarts et disponibilités avant l'enregistrement."
            />

            <Box sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead sx={{ backgroundColor: SALES_UI.soft }}>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Produit</TableCell>
                    <TableCell>Prix référence</TableCell>
                    <TableCell>Prix proposé</TableCell>
                    <TableCell>Écart</TableCell>
                    <TableCell>Qté</TableCell>
                    <TableCell>Total</TableCell>
                    {!isAgentUser && <TableCell>Stock</TableCell>}
                    {!isAgentUser && <TableCell>Statut</TableCell>}
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {cart.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAgentUser ? 8 : 10}>
                        <Typography color="text.secondary">
                          Aucun article dans le panier
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    cart.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.product_code}</TableCell>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{formatAmount(item.base_price)}</TableCell>
                        <TableCell>
                          <Chip
                            label={formatAmount(item.price)}
                            size="small"
                            color={item.price_mode === "agent_proposed" ? "info" : "primary"}
                          />
                        </TableCell>
                        <TableCell>
                          {Math.abs(toNumber(item.price) - toNumber(item.base_price)) > 0.005 ? (
                            <Chip
                              label={`${item.price < item.base_price ? "-" : "+"}${formatAmount(
                                Math.abs(toNumber(item.price) - toNumber(item.base_price))
                              )}`}
                              color={item.price < item.base_price ? "warning" : "info"}
                              size="small"
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{formatAmount(item.total)}</TableCell>

                        {!isAgentUser && (
                          <TableCell>{toNumber(item.stock).toFixed(0)}</TableCell>
                        )}

                        {!isAgentUser && (
                          <TableCell>
                            {item.stock_warning ? (
                              <Chip
                                label={
                                  saleMode === "order"
                                    ? `Commande (${item.negative_after_sale})`
                                    : `Insuffisant (${item.negative_after_sale})`
                                }
                                color={saleMode === "order" ? "warning" : "error"}
                                size="small"
                              />
                            ) : (
                              <Chip label="OK" color="success" size="small" />
                            )}
                          </TableCell>
                        )}

                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleEditItem(item)}
                              disabled={savingSale}
                            >
                              Edit
                            </Button>

                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={() => removeItem(item.id)}
                              disabled={savingSale}
                            >
                              Remove
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>

            <Divider />

            {!isAgentUser && (
              <TextField
                fullWidth
                label="TVA (%)"
                type="number"
                value={tva}
                onChange={(e) => setTva(e.target.value)}
                slotProps={{
                  htmlInput: {
                    min: 0,
                    step: "0.01",
                  },
                }}
                disabled={savingSale}
                helperText="Par défaut 0 %. Les prix sont considérés comme prix saisis."
              />
            )}

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: SALES_UI.soft,
                border: `1px solid ${SALES_UI.border}`,
              }}
            >
              <Typography variant="body1" mb={0.5}>
                Total articles: <strong>{totalItems}</strong>
              </Typography>

              {!isAgentUser && (
                <>
                  <Typography variant="body1" mb={0.5}>
                    Total HT: <strong>{formatAmount(totalHT)}</strong>
                  </Typography>

                  <Typography variant="body1" mb={0.5}>
                    TVA: <strong>{formatAmount(tvaAmount)}</strong>
                  </Typography>
                </>
              )}

              <Typography variant="h5" fontWeight="bold" mt={1}>
                {isAgentUser ? "Montant proposé:" : "Total commande/vente:"}{" "}
                {formatAmount(totalTTC)}
              </Typography>

              {!isAgentUser && (
                <>
                  <Typography variant="body1" mt={1}>
                    Payé maintenant:{" "}
                    <strong>{formatAmount(calculatedPaidAmount)}</strong>
                  </Typography>

                  <Typography variant="body1">
                    Reste à payer: <strong>{formatAmount(remainingAmount)}</strong>
                  </Typography>

                  {overpaidAmount > 0 && (
                    <Typography variant="body1" color="error" mt={1}>
                      Dépassement interdit:{" "}
                      <strong>{formatAmount(overpaidAmount)}</strong>
                    </Typography>
                  )}
                </>
              )}

              {isAgentUser && (
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Ce montant n'est pas encore une vente validée. Il sera confirmé
                  ou ajusté par Entreprise principale.
                </Typography>
              )}
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{
                "& .MuiButton-root": {
                  minHeight: 44,
                  borderRadius: 2.5,
                  fontWeight: 800,
                },
              }}
            >
              <Button
                variant="contained"
                onClick={saveSale}
                disabled={savingSale || cart.length === 0 || !finalStoreSelected}
              >
                {savingSale
                  ? "Enregistrement..."
                  : isAgentUser
                  ? "Enregistrer opportunité terrain"
                  : saleMode === "order"
                  ? "Enregistrer commande"
                  : "Enregistrer vente"}
              </Button>

              <Button
                variant="outlined"
                color="inherit"
                onClick={resetSaleForm}
                disabled={savingSale}
              >
                Réinitialiser
              </Button>

              {embedded && typeof onClose === "function" ? (
                <Button
                  variant="text"
                  color="inherit"
                  onClick={onClose}
                  disabled={savingSale}
                >
                  Annuler
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  )
}